// Native-only app-update check (backlog MOBILE-16). Web is deliberately
// untouched — every page load on snapdraw.app is always the current
// build, nothing to check. Two thresholds per platform
// (app_version_config, migration 0072), not a per-release history table —
// decided with the user 2026-09-22 to start simple; direct SQL is the
// only way to edit these for v1.
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { supabase } from "@/lib/supabase";

export type AppVersionStatus =
  | { kind: "ok" }
  | { kind: "recommended"; message: string | null; latestVersion: string }
  | { kind: "required"; message: string | null };

/**
 * Plain numeric segment comparison, not a string/lexicographic one — "1.9"
 * must sort below "1.10". Segment counts don't need to match ("1.0" vs
 * "1.0.0"): a missing trailing segment is treated as 0.
 */
function compareVersions(a: string, b: string): number {
  const as = a.split(".").map((n) => parseInt(n, 10) || 0);
  const bs = b.split(".").map((n) => parseInt(n, 10) || 0);
  const len = Math.max(as.length, bs.length);
  for (let i = 0; i < len; i++) {
    const diff = (as[i] ?? 0) - (bs[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

/**
 * Resolves to `{ kind: "ok" }` immediately on web or if anything about the
 * check fails — this is a nudge, never something that should be able to
 * break the app over a network hiccup or a platform this table doesn't
 * (yet) have a row for.
 */
export async function checkAppVersion(): Promise<AppVersionStatus> {
  if (!Capacitor.isNativePlatform()) return { kind: "ok" };
  const platform = Capacitor.getPlatform();
  if (platform !== "ios" && platform !== "android") return { kind: "ok" };

  try {
    const [{ version: running }, { data: config, error }] = await Promise.all([
      App.getInfo(),
      supabase
        .from("app_version_config")
        .select("min_required_version, latest_version, message")
        .eq("platform", platform)
        .maybeSingle(),
    ]);
    if (error || !config) return { kind: "ok" };

    if (config.min_required_version && compareVersions(running, config.min_required_version) < 0) {
      return { kind: "required", message: config.message };
    }
    if (compareVersions(running, config.latest_version) < 0) {
      return { kind: "recommended", message: config.message, latestVersion: config.latest_version };
    }
    return { kind: "ok" };
  } catch {
    return { kind: "ok" };
  }
}
